import * as i0 from '@angular/core';
import { Injectable, Optional, Inject, InjectionToken, PLATFORM_ID, NgModule } from '@angular/core';
import { isPlatformBrowser, CommonModule, isPlatformServer } from '@angular/common';
import * as i1$2 from '@ngrx/store';
import { createAction, props, createReducer, on, createFeatureSelector, createSelector, StoreModule } from '@ngrx/store';
import * as i1$1 from '@ngrx/effects';
import { createEffect, ofType, EffectsModule } from '@ngrx/effects';
import { of } from 'rxjs';
import { switchMap, map, catchError, tap, filter, take } from 'rxjs/operators';
import * as i1 from '@angular/common/http';
import { RouterModule } from '@angular/router';

const LoadLanguages = createAction('[Language] Load Languages');
// export const LoadLanguagesSuccess = createAction('[Language] Load Language Success', props<{ languages: Language[] }>());
const LoadLanguagesSuccess = createAction('[Language] Load Language Success', props());
const LoadLanguagesFail = createAction('[Language] Load Languages Fail', props());
const SetActiveLanguage = createAction('[Language] Set Active Language', props());
const SetActiveLanguageSuccess = createAction('[Language] Set Active Language Success', props());
const InitActiveLanguage = createAction('[Language] Initialize selected language');
const InitActiveLanguageComplete = createAction('[Language] Initialize selected language Complete', props());

class LanguageService {
    constructor(http, BASE_URL) {
        this.http = http;
        this.BASE_URL = BASE_URL;
    }
    getLanguages() {
        return this.http.get(`${this.BASE_URL}api/languages`);
    }
}
LanguageService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: LanguageService, deps: [{ token: i1.HttpClient }, { token: 'BASE_URL', optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
LanguageService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: LanguageService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: LanguageService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () {
        return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                        type: Optional
                    }, {
                        type: Inject,
                        args: ['BASE_URL']
                    }] }];
    } });

/**
 * This is not a real service, but it looks like it from the outside.
 * It's just an InjectionTToken used to import the config object, provided from the outside
 */
const LanguageConfigService = new InjectionToken('LanguageConfig');

class LanguageEffects {
    constructor(actions$, langService, platformId, BASE_URL) {
        this.actions$ = actions$;
        this.langService = langService;
        this.platformId = platformId;
        this.BASE_URL = BASE_URL;
        this.STORAGE_KEY = 'LANGUAGE';
        this.LoadLanguages$ = createEffect(() => {
            return this.actions$.pipe(ofType(LoadLanguages), switchMap(() => this.langService.getLanguages().pipe(map(languages => LoadLanguagesSuccess({ languages })), catchError(error => of(LoadLanguagesFail({ error }))))));
        });
        this.setActiveLanguage$ = createEffect(() => {
            return this.actions$.pipe(ofType(SetActiveLanguage), map(({ activeLang }) => {
                if (isPlatformBrowser(this.platformId)) {
                    localStorage.setItem(this.STORAGE_KEY, activeLang);
                    location.reload();
                }
                // this.lng.setLanguage(activeLang);
                return SetActiveLanguageSuccess({ activeLang });
            }));
        });
        this.InitLanguageSettings$ = createEffect(() => {
            return this.actions$.pipe(ofType(InitActiveLanguage), map(() => {
                if (!isPlatformBrowser(this.platformId)) {
                    return InitActiveLanguageComplete({ activeLang: 'en-US' });
                }
                const storedLanguageSettings = localStorage.getItem(this.STORAGE_KEY);
                if (storedLanguageSettings) {
                    return InitActiveLanguageComplete({ activeLang: storedLanguageSettings });
                }
                return InitActiveLanguageComplete({ activeLang: 'en-US' });
            }));
        });
    }
}
LanguageEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: LanguageEffects, deps: [{ token: i1$1.Actions }, { token: LanguageService }, { token: PLATFORM_ID }, { token: 'BASE_URL', optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
LanguageEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: LanguageEffects });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: LanguageEffects, decorators: [{
            type: Injectable
        }], ctorParameters: function () {
        return [{ type: i1$1.Actions }, { type: LanguageService }, { type: undefined, decorators: [{
                        type: Inject,
                        args: [PLATFORM_ID]
                    }] }, { type: undefined, decorators: [{
                        type: Optional
                    }, {
                        type: Inject,
                        args: ['BASE_URL']
                    }] }];
    } });

const effects = [LanguageEffects];

function ToEntities(a, key, initialEntities) {
    return a.reduce((e, obj) => {
        return Object.assign(Object.assign({}, e), { [obj[key]]: obj });
    }, Object.assign({}, initialEntities));
}

const initialState = {
    entities: {},
    active: 'de-DE',
    initialized: false,
    initializing: false,
    loaded: false,
    loading: false,
};
const LanguageReducer = createReducer(initialState, on(LoadLanguagesSuccess, (state, { languages }) => {
    const entities = ToEntities(languages, 'id', state.entities);
    return Object.assign(Object.assign({}, state), { entities, loading: false, loaded: true });
}), on(LoadLanguagesFail, (state, { error }) => {
    return Object.assign(Object.assign({}, state), { loading: false, loaded: false, error });
}), on(InitActiveLanguage, (state) => {
    return Object.assign(Object.assign({}, state), { initializing: true });
}), on(InitActiveLanguageComplete, (state, { activeLang }) => {
    return Object.assign(Object.assign({}, state), { active: activeLang, initializing: false, initialized: true });
}), on(SetActiveLanguageSuccess, (state, { activeLang }) => {
    return Object.assign(Object.assign({}, state), { active: activeLang });
}));
const getLanguagesEntities$1 = (state) => state.entities;
const getActiveLanguage$1 = (state) => state.active;
const getLanguagesLoading = (state) => state.loading;
const getLanguagesLoaded = (state) => state.loaded;
const getLanguagesInitializing$1 = (state) => state.initializing;
const getLanguagesInitialized$1 = (state) => state.initialized;

const featureKey = 'languages';
const reducers = {
    languages: LanguageReducer,
};
const getLanguageState = createFeatureSelector(featureKey);

const getLanguageSelectorState = createSelector(getLanguageState, (state) => state.languages);
const getLanguagesEntities = createSelector(getLanguageSelectorState, getLanguagesEntities$1);
const getActiveLanguage = createSelector(getLanguageSelectorState, getActiveLanguage$1);
const getLanguesLoaded = createSelector(getLanguageSelectorState, getLanguagesLoaded);
const getLanguesLoading = createSelector(getLanguageSelectorState, getLanguagesLoading);
const getLanguagesInitialized = createSelector(getLanguageSelectorState, getLanguagesInitialized$1);
const getLanguagesInitializing = createSelector(getLanguageSelectorState, getLanguagesInitializing$1);
const getAllLanguages = createSelector(getLanguagesEntities, entities => {
    return Object.keys(entities).map(id => entities[id]);
});
const getOrderedLanguages = createSelector(getAllLanguages, languages => {
    return languages.sort((a, b) => a.name.localeCompare(b.name));
});
const getSwitchableLanguages = createSelector(getOrderedLanguages, languages => {
    return languages.filter(a => a.inSwitcher);
});

class LanguageNgrxModule {
    static forRoot(config) {
        config = Object.assign({ defaultLanguage: 'nl-NL', localStorageKey: 'LANGUAGE' }, config);
        return {
            ngModule: LanguageNgrxModule,
            providers: [
                LanguageService,
                {
                    provide: LanguageConfigService,
                    useValue: config,
                },
            ],
        };
    }
}
LanguageNgrxModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: LanguageNgrxModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
LanguageNgrxModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: LanguageNgrxModule, imports: [CommonModule, i1$2.StoreFeatureModule, RouterModule, i1$1.EffectsFeatureModule] });
LanguageNgrxModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: LanguageNgrxModule, imports: [[
            CommonModule,
            StoreModule.forFeature(featureKey, reducers),
            RouterModule,
            EffectsModule.forFeature(effects),
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: LanguageNgrxModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        StoreModule.forFeature(featureKey, reducers),
                        RouterModule,
                        EffectsModule.forFeature(effects),
                    ],
                }]
        }] });

class LanguageGuard {
    constructor(store) {
        this.store = store;
    }
    canActivate() {
        return this.checkStore().pipe(switchMap(() => of(true)), catchError(() => of(false)));
    }
    checkStore() {
        return this.store.select(getLanguesLoaded).pipe(tap(loaded => {
            if (!loaded) {
                this.store.dispatch(LoadLanguages());
            }
        }), filter(loaded => loaded), take(1));
    }
}
LanguageGuard.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: LanguageGuard, deps: [{ token: i1$2.Store }], target: i0.ɵɵFactoryTarget.Injectable });
LanguageGuard.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: LanguageGuard, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: LanguageGuard, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1$2.Store }]; } });

class ActiveLanguageGuard {
    constructor(store, platformId) {
        this.store = store;
        this.platformId = platformId;
    }
    canActivate() {
        return this.checkStore().pipe(switchMap(() => of(true)), catchError(() => of(false)));
    }
    checkStore() {
        if (isPlatformServer(this.platformId)) {
            return of(true);
        }
        return this.store.select(getLanguagesInitialized).pipe(tap(loaded => {
            if (!loaded) {
                this.store.dispatch(InitActiveLanguage());
            }
        }), filter(loaded => loaded), map(loaded => loaded), take(1));
    }
}
ActiveLanguageGuard.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: ActiveLanguageGuard, deps: [{ token: i1$2.Store }, { token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Injectable });
ActiveLanguageGuard.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: ActiveLanguageGuard, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: ActiveLanguageGuard, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () {
        return [{ type: i1$2.Store }, { type: undefined, decorators: [{
                        type: Inject,
                        args: [PLATFORM_ID]
                    }] }];
    } });

class LanguageInterceptor {
    constructor(store, platformId) {
        this.store = store;
        this.platformId = platformId;
    }
    intercept(req, next) {
        return this.store.select(getActiveLanguage).pipe(take(1), switchMap(lang => next.handle(req.clone({
            headers: req.headers.set('Language', `${lang}`),
        }))));
    }
}
LanguageInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: LanguageInterceptor, deps: [{ token: i1$2.Store }, { token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Injectable });
LanguageInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: LanguageInterceptor });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.3", ngImport: i0, type: LanguageInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: function () {
        return [{ type: i1$2.Store }, { type: undefined, decorators: [{
                        type: Inject,
                        args: [PLATFORM_ID]
                    }] }];
    } });

/*
 * Public API Surface of dictionary
 */

/**
 * Generated bundle index. Do not edit.
 */

export { ActiveLanguageGuard, InitActiveLanguage, InitActiveLanguageComplete, LanguageConfigService, LanguageEffects, LanguageGuard, LanguageInterceptor, LanguageNgrxModule, LanguageService, LoadLanguages, LoadLanguagesFail, LoadLanguagesSuccess, SetActiveLanguage, SetActiveLanguageSuccess, effects, featureKey, getActiveLanguage, getAllLanguages, getLanguageSelectorState, getLanguageState, getLanguagesEntities, getLanguagesInitialized, getLanguagesInitializing, getLanguesLoaded, getLanguesLoading, getOrderedLanguages, getSwitchableLanguages, reducers };
